/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	ISchemaValueModel,
	SCHEMA_VALUE_TYPE,
	TGenericBlueprintSchema,
	TGenericModelNode
} from "@adapptio/lib-blueprint";
import {
	IInspectorNode_Default,
	INSPECTOR_NODE_TYPE,
	isInspectorNodeDefault,
	isInspectorNodeList,
	isInspectorNodeMap,
	TInspectorNode
} from "../InspectorNodeTypes";
import { IInspectorControl_ValueType, INSPECTOR_CONTROL_TYPE, TInspectorControl } from "../controls";
import { mapModelToNodes } from "../index";
import { getParentComponentName, hasExpressionAllowed, shouldPassThrough } from "../utils";
import { mapperDescriptor } from "../../modules/Core/ReferenceGuide/mappers/mapperDescriptor";
import { mapperExamples } from "../../modules/Core/ReferenceGuide/mappers/mapperExamples";

function getValueModelControl(model: ISchemaValueModel<TGenericBlueprintSchema>): TGenericModelNode {
	switch (model.type) {
		case SCHEMA_VALUE_TYPE.CONST:
			return model.constant;
		case SCHEMA_VALUE_TYPE.EXPRESSION:
			return model.expression;
		case SCHEMA_VALUE_TYPE.TRANSLATE:
			return model.translate;
		default:
			throw new Error(`Unknown value model type ${model.type}`);
	}
}

export function valueMapper(model: ISchemaValueModel<TGenericBlueprintSchema>): TInspectorNode {
	const controlModel = getValueModelControl(model);
	const controlModelInspectorNode = mapModelToNodes(controlModel);

	const controls: TInspectorControl[] = [];

	const typeControl: IInspectorControl_ValueType = {
		key: String(model.nodeId),
		type: INSPECTOR_CONTROL_TYPE.VALUE_TYPE,
		models: [
			{
				model: model,
				prefix: ""
			}
		]
	};

	if (shouldPassThrough(controlModel)) {
		return {
			...controlModelInspectorNode,
			deps: [ ...controlModelInspectorNode.deps, model ]
		};
	}

	controls.push(...(controlModelInspectorNode?.controls || []));

	if (hasExpressionAllowed(model)) {
		controls.push(typeControl);
	}

	if (isInspectorNodeList(controlModelInspectorNode) || isInspectorNodeMap(controlModelInspectorNode)) {
		return {
			...controlModelInspectorNode,
			key: `${model.nodeId}`,
			deps: [ model, ...controlModelInspectorNode.deps ],
			controls: controls
		};
	}

	const children: IInspectorNode_Default["children"] = [];

	if (isInspectorNodeDefault(controlModelInspectorNode)) {
		children.push(...controlModelInspectorNode.children);
	}

	const deps = [ ...(controlModelInspectorNode?.deps ?? []), controlModel, model ];

	return {
		type: INSPECTOR_NODE_TYPE.DEFAULT,
		key: `${model.nodeId}`,
		label: model.schema.opts.label,
		tooltip: model.schema.opts.description,
		required: model.schema.opts["constraints"]?.required || false,
		default: model.schema.opts["default"] || null,
		referenceData: getParentComponentName(model),
		descriptor: mapperDescriptor(model.schema),
		examples: mapperExamples(model.schema),
		deps: deps,
		options: model.schema.opts["options"],
		controls: controls,
		defaultCollapsed: controlModelInspectorNode.defaultCollapsed,
		collapsedControls: controlModelInspectorNode.collapsedControls,
		children: [ ...children ]
	};
}
